import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { CopyableText } from "@redotech/redo-web/copyable-text";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { useCustomerOrders } from "../hooks/use-customer-orders";
import { ConversationDetailsSection } from "./conversation-details-section";
import { ConversationTicketFieldTitle } from "./conversation-ticket-field-title";
import { CustomerTotals } from "./customer-totals";

export const CustomerInfoSection = memo(function CustomerInfoSection({
  conversation,
}: {
  conversation: ConversationListItemResponse;
}) {
  const customer = conversation.customer;
  const customerEmail = customer?.email;
  const { orders, pending: ordersPending } = useCustomerOrders(customerEmail);

  const profileImageUrl = useMemo(() => {
    if (!customerEmail) {
      return null;
    }
    return getUserAvatarUrl({
      email: customerEmail,
      defaultImage: "404",
      size: Math.ceil(devicePixelRatio * 32),
    });
  }, [customerEmail]);

  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.customer.collapsed"
      title="Customer"
    >
      <Flex dir="column" gap="md">
        <Flex align="center" gap="xs">
          <RedoBadge
            avatar={{
              alt: "Customer profile picture",
              name: customer?.displayName,
              imageUrl: profileImageUrl,
            }}
            color="gray"
            size="sm"
            text={customer?.displayName || "Unknown"}
          />
        </Flex>

        {customerEmail && (
          <Flex dir="column" gap="xs">
            <ConversationTicketFieldTitle>Email</ConversationTicketFieldTitle>
            <CopyableText
              fontSize="xs"
              textColor="tertiary"
              textOverflow="ellipsis"
            >
              {customerEmail}
            </CopyableText>
          </Flex>
        )}

        <CustomerTagsPlaceholder />

        <CustomerTotals orders={orders} pending={ordersPending} />
      </Flex>
    </ConversationDetailsSection>
  );
});

const CustomerTagsPlaceholder = memo(function CustomerTagsPlaceholder() {
  return (
    <Flex dir="column" gap="xs">
      <ConversationTicketFieldTitle>Customer Tags</ConversationTicketFieldTitle>
      <Text fontSize="xs" textColor="tertiary">
        Coming soon
      </Text>
    </Flex>
  );
});
